import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Box, Button, Flex, Icon, Stack, Text, Textarea, useColorModeValue,
} from "@chakra-ui/react";
import { MdCheckCircle, MdEdit, MdSend } from "react-icons/md";
import MissingFieldsEditor from "./MissingFieldsEditor";

/**
 * Builds the API payload for certification approve/update actions. The API
 * recomputes status from admin_message + missing_fields, so `status` is sent
 * for traceability only.
 * @param {string} action - "approve" or "update"
 * @param {string} adminMessage - Admin feedback message
 * @param {Array<string>} missingFields - Admin-edited list of missing fields
 * @param {string} currentStatus - Existing certification status
 * @returns {Object}
 */
function buildCertPayload(action, adminMessage, missingFields, currentStatus) {
    return {
        admin_message: action === "approve" ? "" : adminMessage,
        status: action === "approve" ? "accepted" : currentStatus,
        missing_fields: action === "approve" ? [] : (missingFields || []),
    };
}

/**
 * Sends an approve or update action to the certification API, then notifies the
 * parent and closes. Update requires an admin_message OR a missing field.
 * @param {Object} args - { action, adminMessage, missingFields, certification, setLoading, onUpdated, onClose }
 * @returns {Promise<void>}
 */
async function sendCertAction(args) {
    const { action, adminMessage, missingFields, certification, setLoading, onUpdated, onClose } = args;
    if (action === "update" && !adminMessage && (missingFields || []).length === 0) {
        toast.error("Indiquez un message ou au moins un champ manquant.");
        return;
    }
    setLoading(true);
    try {
        const payload = buildCertPayload(action, adminMessage, missingFields, certification.status);
        const response = await axios.put(`/admin/certifications/update/${certification.id}`, payload);
        toast.success(action === "approve"
            ? "Certification approuvée. Le compte Stripe Connect sera activé si le RIB est renseigné."
            : "Demande de correction envoyée.");
        if (onUpdated) onUpdated(response.data);
        if (onClose) onClose();
    } catch (error) {
        toast.error("Une erreur est survenue lors de la soumission.");
        console.error(error);
    } finally {
        setLoading(false);
    }
}

/**
 * Admin feedback textarea shown in the rejection editor.
 * @param {Object} props
 * @param {string} props.value - Current admin message
 * @param {Function} props.onChange - Message setter
 * @returns {React.ReactElement}
 */
function AdminMessageField({ value, onChange }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box>
            <Text fontSize="sm" fontWeight="600" color={titleColor} mb="6px">Message à l'utilisateur</Text>
            <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
                placeholder="Expliquez ce qui doit être corrigé..." borderRadius="12px" />
        </Box>
    );
}

/**
 * The two primary decision buttons shown by default (idle mode).
 * @param {Object} props
 * @param {boolean} props.loading - Whether the approve request is in progress
 * @param {Function} props.onApprove - Approve handler
 * @param {Function} props.onReject - Switch to rejection editor handler
 * @returns {React.ReactElement}
 */
function DecisionButtons({ loading, onApprove, onReject }) {
    return (
        <Flex gap="10px" wrap="wrap">
            <Button colorScheme="green" leftIcon={<Icon as={MdCheckCircle} />} isLoading={loading}
                loadingText="En cours..." onClick={onApprove} flex="1" minW="140px">
                Approuver
            </Button>
            <Button colorScheme="orange" variant="outline" leftIcon={<Icon as={MdEdit} />}
                onClick={onReject} flex="1" minW="140px">
                Demander une correction
            </Button>
        </Flex>
    );
}

/**
 * The rejection editor (missing fields + message + submit/cancel actions).
 * @param {Object} props - editor state and handlers
 * @returns {React.ReactElement}
 */
function RejectEditor({ adminMessage, setAdminMessage, missingFields, setMissingFields, loading, onCancel, onSubmit }) {
    const canSubmit = !!adminMessage || (missingFields || []).length > 0;
    return (
        <Stack spacing="12px">
            <MissingFieldsEditor fields={missingFields} onChange={setMissingFields} />
            <AdminMessageField value={adminMessage} onChange={setAdminMessage} />
            <Flex gap="10px" justify="flex-end">
                <Button variant="ghost" onClick={onCancel} isDisabled={loading}>Annuler</Button>
                <Button colorScheme="orange" leftIcon={<Icon as={MdSend} />} isLoading={loading}
                    loadingText="Envoi..." isDisabled={!canSubmit} onClick={onSubmit}>
                    Envoyer la demande
                </Button>
            </Flex>
        </Stack>
    );
}

/**
 * Decision panel for a certification: approve directly, or progressively reveal
 * the correction editor (missing fields + message) before sending it back.
 * @param {Object} props
 * @param {Object} props.certification - The certification under review
 * @param {Function} props.onUpdated - Callback receiving the updated cert payload
 * @param {Function} props.onClose - Callback to close the drawer after an action
 * @returns {React.ReactElement}
 */
function CertificationActionPanel({ certification, onUpdated, onClose }) {
    const [loading, setLoading] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [adminMessage, setAdminMessage] = useState(certification?.admin_message || "");
    const [missingFields, setMissingFields] = useState(certification?.missing_fields || []);
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const send = (action) => sendCertAction({ action, adminMessage, missingFields, certification, setLoading, onUpdated, onClose });
    return (
        <Box>
            <Text fontSize="sm" fontWeight="700" color={titleColor} mb="10px">Décision</Text>
            {rejecting ? (
                <RejectEditor adminMessage={adminMessage} setAdminMessage={setAdminMessage}
                    missingFields={missingFields} setMissingFields={setMissingFields} loading={loading}
                    onCancel={() => setRejecting(false)} onSubmit={() => send("update")} />
            ) : (
                <DecisionButtons loading={loading} onApprove={() => send("approve")} onReject={() => setRejecting(true)} />
            )}
        </Box>
    );
}

export default CertificationActionPanel;
